import React from 'react'

import { Page, View, Text, Image } from '@react-pdf/renderer'
import styles from '../style'

import logo from '../assets/logo.png'
import TopBanner from '../Components/TopBanner'
import { ICensusMemberDTO } from '../../../services/quote/dto'

const HalfSectionRow = ({
  image,
  value
}: {
  image: string
  value: string
}) => (
  <View style={styles.row}>
    <View style={[styles.row, { flex: 1 }]}>
      <Image style={{ height: 12, objectFit: 'contain' }} src={image} />
      <Text style={styles.halfSectionValue}>{value}</Text>
    </View>
  </View>
)

interface IProgram {
  name: string
  image: string
}

export interface IRatesPage {
  quoteId: string
  groupName: string
  quoteDetails: {
    effectiveDate: string
    fundingType: string
    contractTerms: string
    agency: string
    cityState: string
    zipCode: string
    pepmPayable?: number | null
  }
  networkProgram: IProgram
  pbmProgram: IProgram
  addOnPrograms: (IProgram & { category: string })[]
  rates: {
    name: string
    employeeOnly: string
    employeePlusSpouse: string
    employeePlusChildren: string
    employeePlusFamily: string
  }[]
  tiersAmount?: Record<string, number>,
  census: ICensusMemberDTO[]
}

const RatesPage = ({
  quoteId,
  groupName,
  quoteDetails,
  networkProgram,
  pbmProgram,
  addOnPrograms,
  rates,
  tiersAmount
}: IRatesPage) => {
  return (
    <Page>
      <TopBanner title="Rates" />
      <View style={styles.pageBody}>
        <Text style={styles.descriptionText}>Quote ID: {quoteId}</Text>
        <Text
          style={{
            marginVertical: 2
          }}
        >
          {groupName}
        </Text>
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>Quote Details</Text>
          <View style={[styles.row, { marginBottom: 10 }]}>
            <View style={styles.rowItem}>
              <Text style={styles.quoteDetailValue}>
                {quoteDetails.effectiveDate}
              </Text>
              <Text style={styles.descriptionText}>Effective Date</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.quoteDetailValue}>
                {quoteDetails.fundingType}
              </Text>
              <Text style={styles.descriptionText}>Funding Type</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.quoteDetailValue}>
                {quoteDetails.contractTerms}
              </Text>
              <Text style={styles.descriptionText}>Contract Terms</Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.rowItem}>
              <Text style={styles.quoteDetailValue}>{quoteDetails.agency}</Text>
              <Text style={styles.descriptionText}>Agency</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.quoteDetailValue}>
                {quoteDetails.cityState}
              </Text>
              <Text style={styles.descriptionText}>City, State</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.quoteDetailValue}>
                {quoteDetails.zipCode}
              </Text>
              <Text style={styles.descriptionText}>ZIP Code</Text>
            </View>
          </View>
        </View>
        <View style={[styles.row, { gap: 10 }]}>
          <View style={[styles.section, { flex: 1, height: '100%' }]}>
            <Text style={styles.sectionHeader}>Network & Pharmacy</Text>
            <View style={{ gap: 3 }}>
              <HalfSectionRow
                image={networkProgram.image}
                value={networkProgram.name}
              />
              <HalfSectionRow
                image={pbmProgram.image}
                value={pbmProgram.name}
              />
            </View>
          </View>
          <View style={[styles.section, { flex: 1, height: '100%' }]}>
            <Text style={styles.sectionHeader}>Add-ons Chosen</Text>
            <View style={{ gap: 3 }}>
              {addOnPrograms.map(({ image, name }) => (
                <HalfSectionRow
                  key={name}
                  image={image}
                  value={name}
                />
              ))}
            </View>
          </View>
        </View>
        <View style={[styles.section, { padding: 0 }]}>
          <View style={[styles.row, { padding: 10 }]}>
            <Image
              src={logo}
              style={{
                width: 36,
                objectFit: 'contain'
              }}
            />
            <Text
              style={[styles.sectionHeader, { marginBottom: 0, marginLeft: 4 }]}
            >
              Rates
            </Text>
          </View>
          <View>
            <View
              style={[
                styles.row,
                { backgroundColor: '#F8F9FA', paddingVertical: 3 }
              ]}
            >
              <View style={styles.rowItem}></View>
              <View style={styles.rowItem}>
                <Text style={styles.descriptionText}>Employee Only</Text>
              </View>
              <View style={styles.rowItem}>
                <Text style={styles.descriptionText}>Employee + Spouse</Text>
              </View>
              <View style={styles.rowItem}>
                <Text style={styles.descriptionText}>Employee + Children</Text>
              </View>
              <View style={styles.rowItem}>
                <Text style={styles.descriptionText}>Employee + Family</Text>
              </View>
            </View>
            <View
              style={[
                styles.row,
                { backgroundColor: '#F8F9FA', paddingVertical: 3 }
              ]}
            >
              <View style={styles.rowItem}>
                <Text style={[styles.descriptionText, { marginLeft: 10 }]}>Members in Tier</Text>
              </View>
              <View style={styles.rowItem}>
                <Text style={styles.descriptionText}>{tiersAmount?.single || '-'}</Text>
              </View>
              <View style={styles.rowItem}>
                <Text style={styles.descriptionText}>{tiersAmount?.employee_spouse || '-'}</Text>
              </View>
              <View style={styles.rowItem}>
                <Text style={styles.descriptionText}>{tiersAmount?.employee_children || '-'}</Text>
              </View>
              <View style={styles.rowItem}>
                <Text style={styles.descriptionText}>{tiersAmount?.family || '-'}</Text>
              </View>
            </View>
            {rates.map((rate, index) => (
              <View
                key={rate.name}
                style={[
                  styles.tableRow,
                  {
                    borderBottom:
                      index === rates.length - 1 ? undefined : '1 solid #E7EAF3'
                  }
                ]}
              >
                <View style={styles.rowItem}>
                  <Text style={[styles.tableCellValue, { marginLeft: 10 }]}>
                    {rate.name}
                  </Text>
                </View>
                <View style={styles.rowItem}>
                  <Text style={styles.tableCellValue}>{rate.employeeOnly}</Text>
                </View>
                <View style={styles.rowItem}>
                  <Text style={styles.tableCellValue}>
                    {rate.employeePlusSpouse}
                  </Text>
                </View>
                <View style={styles.rowItem}>
                  <Text style={styles.tableCellValue}>
                    {rate.employeePlusChildren}
                  </Text>
                </View>
                <View style={styles.rowItem}>
                  <Text style={styles.tableCellValue}>
                    {rate.employeePlusFamily}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>
    </Page>
  )
}

export default RatesPage
